import is from '@sindresorhus/is';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { type FunctionComponent, type KeyboardEvent, useMemo, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { IconCrossSmall, IconTrashCan } from '~/ui/core/icons';
import { RecessedSurface } from '~/ui/core/recessed-surface/RecessedSurface';
import { ScrollFade } from '~/ui/core/scroll-fade/ScrollFade';
import { TileControlGroup } from '~/ui/core/tile/Tile';
import { SplitButton, type SplitButtonOption } from '~/ui/domain/split-button/SplitButton';

import type { Thread } from '../../data-contract/chat';
import { useDeleteChatThread, useIsDeletingChatThread } from '../api';
import { formatThreadAge } from '../thread-age';
import { countThreadsByFilter, filterThreads, THREAD_FILTERS, type ThreadFilter } from '../thread-filters';
import type { ThreadStatus } from '../thread-status';
import { ThreadStatusBadge } from './ThreadStatusBadge';

type ThreadsRailProps = {
    readonly currentThreadId: string | null;
    readonly getStatus: (thread: Thread) => ThreadStatus;
    readonly isUnread: (thread: Thread) => boolean;
    readonly threads: readonly Thread[];
    readonly onClose: () => void;
    /** Called once a delete succeeds, so the host can leave the thread if it is still the open one. */
    readonly onThreadDeleted: (id: string) => void;
    readonly onSelect: (id: string) => void;
};

const FILTER_LABEL_KEYS: Readonly<Record<ThreadFilter, TranslationKey>> = {
    all: 'states.all',
    'needs-input': 'chat.threads-rail.filters.needs-input',
    running: 'chat.threads-rail.filters.running',
    unread: 'chat.threads-rail.filters.unread',
};

const ROW_CONTENT_CLASS_NAME = 'flex w-full items-center gap-s px-xl py-xl';

// Idle rows get the same resting surface and orange hover outline as a skill badge; the open thread
// swaps it for the recessed pressed treatment below.
const IDLE_ROW_SURFACE_CLASS_NAME = clsx(
    'rounded-md outline outline-1 -outline-offset-1 outline-transparent',
    'transition-[outline-color,background-color] duration-200 ease',
    'hover:bg-surface-hover hover:outline-card-selection-edge',
    'has-[:focus-visible]:outline-focus-ring',
);

// The delete button takes no room until the row is hovered or holds focus, so names keep the width.
// The slot only clips while collapsed, so the revealed button's hover shadow is not cut off.
const DELETE_SLOT_CLASS_NAME = clsx(
    'flex w-0 shrink-0 -ml-s overflow-hidden',
    'group-hover/thread-row:ml-0 group-hover/thread-row:w-auto group-hover/thread-row:overflow-visible',
    'group-focus-within/thread-row:ml-0 group-focus-within/thread-row:w-auto group-focus-within/thread-row:overflow-visible',
);

/**
 * The inbox of chat threads that opens beside the conversation from the history button.
 * Rows keep the API order, so threads that need attention stay on top.
 */
export const ThreadsRail: FunctionComponent<ThreadsRailProps> = ({
    currentThreadId,
    getStatus,
    isUnread,
    threads,
    onClose,
    onSelect,
    onThreadDeleted,
}) => {
    const translate = useTranslate();
    const { enqueueSnackbar } = useSnackbar();
    const deleteThread = useDeleteChatThread();
    const [filter, setFilter] = useState<ThreadFilter>('all');
    const [threadPendingDelete, setThreadPendingDelete] = useState<Thread | null>(null);

    const counts = useMemo(
        () => countThreadsByFilter({ getStatus, isUnread, threads }),
        [getStatus, isUnread, threads],
    );
    const visibleThreads = useMemo(
        () => filterThreads({ filter, getStatus, isUnread, threads }),
        [filter, getStatus, isUnread, threads],
    );

    const filterOptions = useMemo<readonly SplitButtonOption[]>(
        () =>
            THREAD_FILTERS.map((option) => ({
                label: (
                    <>
                        {translate(FILTER_LABEL_KEYS[option])}
                        {option === 'all' ? null : <span className="tabular-nums"> {counts[option]}</span>}
                    </>
                ),
                value: option,
            })),
        [counts, translate],
    );

    const handleFilterChange = (value: string): void => {
        const nextFilter = THREAD_FILTERS.find((option) => option === value);
        if (!is.undefined(nextFilter)) setFilter(nextFilter);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
        if (event.key !== 'Escape' || !is.null(threadPendingDelete)) return;
        event.stopPropagation();
        onClose();
    };

    // Each delete awaits its own promise: mutate() callbacks only fire for the latest call, so a second
    // delete started before the first finished would drop the first one's outcome.
    const confirmDelete = async (): Promise<void> => {
        if (is.null(threadPendingDelete)) return;
        const { id } = threadPendingDelete;
        setThreadPendingDelete(null);
        try {
            await deleteThread.mutateAsync({ threadId: id });
        } catch {
            enqueueSnackbar(translate('chat.threads-rail.delete-error'), { variant: 'error' });
            return;
        }
        onThreadDeleted(id);
    };

    return (
        <aside
            aria-label={translate('chat.threads-rail.title')}
            className="flex h-full min-h-0 flex-col overflow-hidden rounded-xxxl border border-border-subtle bg-surface-raised font-sans"
            data-testid="chat-threads-rail"
            onKeyDown={handleKeyDown}
        >
            <div className="flex shrink-0 items-center gap-s px-xxl pb-s pt-tile-edge">
                <TileControlGroup>
                    <Button
                        aria-label={translate('common.close')}
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        onClick={onClose}
                    >
                        <IconCrossSmall />
                    </Button>
                </TileControlGroup>
                <h2 className="m-0 min-w-0 flex-1 truncate text-body-s font-medium text-text-primary tracking-tight">
                    {translate('chat.threads-rail.title')}
                </h2>
            </div>
            <ScrollFade className="flex shrink-0 px-xxl pb-s" orientation="horizontal">
                <div className="py-xs">
                    <SplitButton label="" options={filterOptions} value={filter} onOptionChange={handleFilterChange} />
                </div>
            </ScrollFade>
            <ScrollFade className="flex min-h-0 flex-1 flex-col gap-xs px-m pb-m" orientation="vertical">
                {visibleThreads.map((thread) => (
                    <ThreadsRailRow
                        key={thread.id}
                        isCurrent={thread.id === currentThreadId}
                        isUnread={isUnread(thread)}
                        status={getStatus(thread)}
                        thread={thread}
                        onDelete={() => setThreadPendingDelete(thread)}
                        onSelect={() => onSelect(thread.id)}
                    />
                ))}
            </ScrollFade>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.delete')}
                confirmButtonVariant="destructive"
                description={translate('chat.threads-rail.delete-description')}
                open={!is.null(threadPendingDelete)}
                title={translate('chat.threads-rail.delete-title')}
                onCancel={() => setThreadPendingDelete(null)}
                onConfirm={() => void confirmDelete()}
            />
        </aside>
    );
};

type ThreadsRailRowProps = {
    readonly isCurrent: boolean;
    readonly isUnread: boolean;
    readonly status: ThreadStatus;
    readonly thread: Thread;
    readonly onDelete: () => void;
    readonly onSelect: () => void;
};

const ThreadsRailRow: FunctionComponent<ThreadsRailRowProps> = ({
    isCurrent,
    isUnread,
    status,
    thread,
    onDelete,
    onSelect,
}) => {
    const translate = useTranslate();
    const { applicationState } = useApplicationContext();
    const title = is.nonEmptyString(thread.name) ? thread.name : translate('chat.untitled-thread');
    const isDeleting = useIsDeletingChatThread(thread.id);
    const age = formatThreadAge(
        thread.updatedAt,
        applicationState.clock.getCurrentTimestamp(),
        applicationState.language.code,
    );

    return (
        <div
            className={clsx('group/thread-row relative', isDeleting && 'pointer-events-none opacity-50')}
            data-current={isCurrent}
            data-testid="chat-threads-rail-row"
        >
            <RecessedSurface
                active={isCurrent}
                className={isCurrent ? undefined : IDLE_ROW_SURFACE_CLASS_NAME}
                pocketTone="orange"
                radius="md"
                renderMode="overlay"
                surfaceClassName={ROW_CONTENT_CLASS_NAME}
                surfaceShadow="sm"
                surfaceTone="gray"
                transition="none"
            >
                <button
                    aria-current={isCurrent ? 'true' : undefined}
                    className="absolute inset-0 cursor-pointer rounded-md border-0 bg-transparent p-0 outline-none disabled:cursor-default"
                    data-testid="chat-threads-rail-row-select"
                    disabled={isCurrent || isDeleting}
                    type="button"
                    onClick={onSelect}
                >
                    <span className="sr-only">{title}</span>
                </button>
                <span className="pointer-events-none flex min-w-0 flex-1 items-center gap-s">
                    <span
                        aria-hidden={true}
                        className={clsx(
                            'min-w-0 truncate text-body-xs font-medium',
                            isCurrent ? 'text-text-selected' : 'text-text-content-strong',
                        )}
                        data-testid="chat-threads-rail-row-title"
                    >
                        {title}
                    </span>
                    {isUnread ? (
                        <span
                            aria-hidden={true}
                            className="inline-block size-2 shrink-0 rounded-full bg-accent-soft"
                            data-testid="thread-unseen-dot"
                        />
                    ) : null}
                </span>
                {isUnread ? <span className="sr-only">{translate('chat.thread-status.unseen')}</span> : null}
                <span
                    className={clsx(
                        'pointer-events-none shrink-0 text-body-xxs',
                        isCurrent ? 'text-text-selected' : 'text-text-content-subtle',
                    )}
                >
                    {age}
                </span>
                <span className="pointer-events-none flex shrink-0 items-center">
                    <ThreadStatusBadge status={status} />
                </span>
                <span className={DELETE_SLOT_CLASS_NAME}>
                    <Button
                        aria-label={translate('common.delete-named', { name: title })}
                        className="relative"
                        data-testid="chat-threads-rail-row-delete"
                        disabled={isDeleting}
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        onClick={onDelete}
                    >
                        <IconTrashCan />
                    </Button>
                </span>
            </RecessedSurface>
        </div>
    );
};
